import { useFilters, getAllDescendantIds } from '@/contexts/FilterContext';
import { useUnitStatusOptional } from '@/contexts/UnitStatusContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useUserRole } from '@/hooks/useUserRole';
import { useHierarchicalSearch } from '@/hooks/useHierarchicalSearch';
import { useAuth } from '@/contexts/AuthContext';
import { getLabelForUser, getIconForUser } from '@/lib/labels';
import { useFilterPermissions } from '@/hooks/useFilterPermissions';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { getIconComponent } from '@/lib/iconUtils';
import { preFilterUnits } from '@/lib/filterUtils';
import type { Unit, UnitFilterData, IndustryType, Company, GroupWithChildren } from '@/types';
import type { IndustryLabel } from '@/contexts/SettingsContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Building2, Users, ChevronDown, ChevronRight, CornerDownRight, ArrowDownWideNarrow, ArrowDownAZ, Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useMemo, RefObject } from 'react';

// Default labels matching SettingsContext defaults
// These are used when SettingsContext is not available (e.g., Dashboard)
const DEFAULT_LABELS: IndustryLabel[] = [
  { id: '1', industry: 'film', label_type: 'group', label_value: 'Production', icon: null, superadmin_override: false, admin_override: false },
  { id: '2', industry: 'default', label_type: 'group', label_value: 'Group', icon: null, superadmin_override: false, admin_override: false },
];

interface CompanyGroupFiltersProps {
  units?: (Unit | UnitFilterData)[];
  /** Override: selected group IDs (for sidebar mode with separate state) */
  selectedGroupIds?: string[];
  /** Override: callback when groups change (for sidebar mode) */
  onGroupsChange?: (groupIds: string[]) => void;
  /** Override: selected industry types (for sidebar mode) */
  selectedIndustryTypes?: IndustryType[];
  /** When true, this is sidebar mode - hides company filter, adjusts styling */
  sidebarMode?: boolean;
  /** When true, hide the GROUP filter (used in Reports status bar where GROUP is in sidebar) */
  hideGroupFilter?: boolean;
  /** Search query for filtering dropdown results and auto-expanding matching groups */
  searchQuery?: string;
  /** When true, renders just the group tree content without dropdown/trigger (for embedding in Popovers) */
  embeddedMode?: boolean;
  /** When true, highlights the company filter with ring effect */
  highlightCompany?: boolean;
  /** When true, highlights the group filter with ring effect */
  highlightGroup?: boolean;
  /** Ref for the company filter button (for tooltip positioning) */
  companyButtonRef?: RefObject<HTMLButtonElement | null>;
  /** Ref for the group filter button (for tooltip positioning) */
  groupButtonRef?: RefObject<HTMLButtonElement | null>;
}

export function CompanyGroupFilters({
  units = [],
  selectedGroupIds: propsSelectedGroupIds,
  onGroupsChange,
  selectedIndustryTypes: propsSelectedIndustryTypes,
  sidebarMode = false,
  hideGroupFilter = false,
  searchQuery = '',
  embeddedMode = false,
  highlightCompany = false,
  highlightGroup = false,
  companyButtonRef,
  groupButtonRef,
}: CompanyGroupFiltersProps) {
  const isMobile = useIsMobile();
  useAuth();
  const { role, isGroupAdmin } = useUserRole();
  const { showCompanyFilter, showGroupFilter } = useFilterPermissions();
  const { labels = [] } = useSettingsContext();

  // Use hierarchical search to get matching company/group IDs and unit matcher
  const { expandedGroupIds: searchExpandedGroupIds, matchesUnit } = useHierarchicalSearch(searchQuery);

  const [companySortBy, setCompanySortBy] = useState<'name' | 'count'>('name');
  const [groupSortBy, setGroupSortBy] = useState<'name' | 'count'>('name');
  // Track which groups are manually expanded on mobile (collapsible sections instead of pop-out submenus)
  const [manuallyExpandedGroupIds, setManuallyExpandedGroupIds] = useState<Set<string>>(new Set());
  // Controlled state for Company and Group dropdowns/sheets
  const [companyMenuOpen, setCompanyMenuOpen] = useState(false);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);

  // Merge manually expanded groups with search-expanded groups for effective expansion
  const expandedGroupIds = useMemo(() => {
    if (!searchExpandedGroupIds || searchExpandedGroupIds.size === 0) {
      return manuallyExpandedGroupIds;
    }
    // When searching, auto-expand matching groups plus any manually expanded
    const merged = new Set(manuallyExpandedGroupIds);
    searchExpandedGroupIds.forEach(id => merged.add(id));
    return merged;
  }, [manuallyExpandedGroupIds, searchExpandedGroupIds]);
  const {
    selectedCompanyIds,
    selectedGroupIds: contextSelectedGroupIds,
    selectedIndustryTypes: contextSelectedIndustryTypes,
    companies,
    groups,
    groupTree,
    units: contextUnits,
    setSelectedCompanyIds,
    setSelectedGroupIds,
    toggleGroupWithCascade: contextToggleGroupWithCascade,
    getGroupSelectionState: contextGetGroupSelectionState,
    isLoading: filterContextLoading,
  } = useFilters();
  const _unitStatusContext = useUnitStatusOptional();

  // Use props if provided (sidebar mode), otherwise use context (status bar mode)
  const selectedGroupIds = propsSelectedGroupIds ?? contextSelectedGroupIds;
  const selectedIndustryTypes = propsSelectedIndustryTypes ?? contextSelectedIndustryTypes;

  // PERFORMANCE: Memoized group lookup map for O(1) access instead of O(n) .find() calls
  // This is used by getAllAncestorIds and effectiveSelectedGroupCount calculations
  const groupById = useMemo(() => {
    const map = new Map<string, typeof groups[0]>();
    groups.forEach(g => map.set(g.id, g));
    return map;
  }, [groups]);

  // Helper to set selected groups - uses prop callback in sidebar mode, context in status bar mode
  const updateSelectedGroupIds = (groupIds: string[]) => {
    if (onGroupsChange) {
      onGroupsChange(groupIds);
    } else {
      setSelectedGroupIds(groupIds);
    }
  };

  // Helper to get all ancestor IDs for a group (optimized with memoized lookup map)
  const getAllAncestorIds = (groupId: string): string[] => {
    const ancestors: string[] = [];
    const group = groupById.get(groupId);
    if (!group) return ancestors;

    let currentParentId: string | null | undefined = group.parent_group_id;
    while (currentParentId) {
      ancestors.push(currentParentId);
      const parent = groupById.get(currentParentId);
      currentParentId = parent?.parent_group_id ?? null;
    }
    return ancestors;
  };

  // Helper to toggle group with cascade - adapts for sidebar mode
  const toggleGroupWithCascade = (groupId: string) => {
    if (onGroupsChange) {
      // In sidebar mode, we need to manually handle the cascade toggle
      // Use imported getAllDescendantIds and add the group's own ID
      const descendantIds = [groupId, ...getAllDescendantIds(groupId, groupTree)];
      const ancestorIds = getAllAncestorIds(groupId);

      // Check if this is an unassigned pseudo-group (starts with __unassigned_)
      const isUnassignedGroup = groupId.startsWith('__unassigned_');
      if (isUnassignedGroup) {
        // Unassigned groups are just toggled without cascade
        if (selectedGroupIds.includes(groupId)) {
          onGroupsChange(selectedGroupIds.filter(id => id !== groupId));
        } else {
          onGroupsChange([...selectedGroupIds, groupId]);
        }
        return;
      }

      const allSelected = descendantIds.every(id => selectedGroupIds.includes(id));

      if (allSelected) {
        // Deselect all descendants (but keep ancestors selected if they have other selected children)
        onGroupsChange(selectedGroupIds.filter(id => !descendantIds.includes(id)));
      } else {
        // Select all descendants AND all ancestors (parent chain)
        const newSelection = new Set(selectedGroupIds);
        descendantIds.forEach(id => newSelection.add(id));
        ancestorIds.forEach(id => newSelection.add(id));
        onGroupsChange(Array.from(newSelection));
      }
    } else {
      contextToggleGroupWithCascade(groupId);
    }
  };

  // Helper to get group selection state - adapts for sidebar mode
  const getGroupSelectionState = (group: GroupWithChildren) => {
    if (onGroupsChange) {
      // In sidebar mode, compute selection state from props
      // Use imported getAllDescendantIds and add the group's own ID
      const descendantIds = [group.id, ...getAllDescendantIds(group.id, groupTree)];
      const selectedCount = descendantIds.filter(id => selectedGroupIds.includes(id)).length;

      return {
        selected: selectedCount === descendantIds.length,
        partiallySelected: selectedCount > 0 && selectedCount < descendantIds.length,
        selectedChildCount: selectedCount,
        childCount: descendantIds.length,
      };
    }
    return contextGetGroupSelectionState(group);
  };

  // Get group label and icon using labels from context or defaults
  const allLabels = labels.length > 0 ? labels : DEFAULT_LABELS;
  // Determine industry based on selected companies' industry
  // Show "Production" when all selected companies are film industry, otherwise "Group"
  const companyIndustry = useMemo(() => {
    if (selectedCompanyIds.length === 0 || companies.length === 0) {
      return 'default';
    }
    // Get industry of all selected companies
    const selectedCompaniesIndustry = selectedCompanyIds
      .map(id => companies.find(c => c.id === id)?.industry)
      .filter(Boolean);
    // If all selected companies are film industry, use 'film' label
    if (selectedCompaniesIndustry.length > 0 &&
        selectedCompaniesIndustry.every(t => t === 'film')) {
      return 'film';
    }
    return 'default';
  }, [selectedCompanyIds, companies]);
  const userRole: 'superadmin' | 'admin' | 'user' =
    role === 'superadmin' ? 'superadmin' :
    (role === 'admin' || role === 'group_admin') ? 'admin' : 'user';

  const groupLabel = useMemo(() => {
    return getLabelForUser('group', userRole, companyIndustry, allLabels);
  }, [userRole, companyIndustry, allLabels]);

  const groupIconName = useMemo(() => {
    return getIconForUser('group', userRole, companyIndustry, allLabels);
  }, [userRole, companyIndustry, allLabels]);

  const GroupIcon = useMemo(() => {
    return getIconComponent(groupIconName) || Users;
  }, [groupIconName]);

  // Check if multiple types are selected (i.e., we should group by type)
  // We now always group by type if any type is selected to show the industry dividers/headers
  const allTypes: IndustryType[] = ['film', 'event', 'gov', 'utility', 'relief'];
  const shouldGroupByType = selectedIndustryTypes.length > 0;

  // Filter units based on selected industry types and search query for counts and display
  // Use prop units if provided, otherwise fall back to context units
  const unitsSource = units.length > 0 ? units : contextUnits;
  const filteredUnits = useMemo(() => {
    // If no industry types selected, return empty (user cleared all selections)
    if (selectedIndustryTypes.length === 0) {
      return [];
    }

    let result = unitsSource;

    // Filter by selected industry types
    result = result.filter((u: Unit | UnitFilterData) => selectedIndustryTypes.includes(u.industry_type || 'film'));

    // Filter by search query (using hierarchical matching for company/group)
    if (searchQuery) {
      result = result.filter((u: Unit | UnitFilterData) => matchesUnit(u as Unit));
    }

    return result;
  }, [unitsSource, selectedIndustryTypes, searchQuery, matchesUnit]);

  // PERFORMANCE: Pre-computed unit counts for O(1) lookups instead of O(n) filter operations
  // This eliminates repeated .filter().length calls throughout the component
  const unitCountMaps = useMemo(() => {
    // Map: companyId -> count
    const byCompany = new Map<string, number>();
    // Map: "companyId:industryType" -> count
    const byCompanyAndType = new Map<string, number>();
    // Map: groupId -> count
    const byGroup = new Map<string, number>();
    // Map: companyId -> unassigned count (units with company but no group)
    const unassignedByCompany = new Map<string, number>();

    filteredUnits.forEach((unit: Unit | UnitFilterData) => {
      const companyId = unit.company_id;
      const industryType = unit.industry_type || 'film';
      // Handle both Unit type (nested primary_group.id) and UnitFilterData type (flat primary_group_id)
      const groupId = unit.primary_group_id || ('primary_group' in unit && unit.primary_group?.id);

      if (companyId) {
        // Count by company
        byCompany.set(companyId, (byCompany.get(companyId) || 0) + 1);

        // Count by company + industry type
        const companyTypeKey = `${companyId}:${industryType}`;
        byCompanyAndType.set(companyTypeKey, (byCompanyAndType.get(companyTypeKey) || 0) + 1);

        // Count unassigned (units with company but no group)
        if (!groupId) {
          unassignedByCompany.set(companyId, (unassignedByCompany.get(companyId) || 0) + 1);
        }
      }

      // Count by group
      if (groupId) {
        byGroup.set(groupId, (byGroup.get(groupId) || 0) + 1);
      }
    });

    return { byCompany, byCompanyAndType, byGroup, unassignedByCompany };
  }, [filteredUnits]);

  // Helper functions for O(1) unit count lookups
  const getUnitCountByCompanyAndType = (companyId: string, type: string): number => {
    return unitCountMaps.byCompanyAndType.get(`${companyId}:${type}`) || 0;
  };

  const getUnitCountByGroup = (groupId: string): number => {
    return unitCountMaps.byGroup.get(groupId) || 0;
  };

  const getUnassignedCountByCompany = (companyId: string): number => {
    return unitCountMaps.unassignedByCompany.get(companyId) || 0;
  };

  const handleCompanyToggle = (companyId: string) => {
    if (selectedCompanyIds.includes(companyId)) {
      setSelectedCompanyIds(selectedCompanyIds.filter((id: string) => id !== companyId));
    } else {
      setSelectedCompanyIds([...selectedCompanyIds, companyId]);
    }
  };

  const handleSelectAllCompanies = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedCompanyIds(companies.map((company) => company.id));
  };

  const handleClearCompanies = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedCompanyIds([]);
    // Close menu immediately after clearing
    setCompanyMenuOpen(false);
  };

  const handleSelectAllGroups = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    updateSelectedGroupIds(groups.map(group => group.id));
  };

  const handleClearGroups = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    updateSelectedGroupIds([]);
    // Close menu immediately after clearing
    setGroupMenuOpen(false);
  };

  // Toggle expanded state for mobile collapsible groups
  const toggleGroupExpanded = (groupId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setManuallyExpandedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // No longer show loading state - filters are cached and loaded immediately

  // Calculate selected group badge count (leaf groups + unassigned)
  const countSelectedLeafGroups = (group: GroupWithChildren): number => {
    if (!group.children || group.children.length === 0) {
      return selectedGroupIds.includes(group.id) ? 1 : 0;
    }
    return group.children.reduce((sum: number, child) => sum + countSelectedLeafGroups(child), 0);
  };
  
  const selectedLeafGroupCount = groupTree.reduce((sum, group) => sum + countSelectedLeafGroups(group), 0);
  const selectedUnassignedCount = companies.filter((company) =>
    selectedGroupIds.includes(`__unassigned_${company.id}__`)
  ).length;
  const _totalSelectedGroupBadgeCount = selectedLeafGroupCount + selectedUnassignedCount;

  // Memoized map of company unit counts to avoid repeated filtering in render loops
  const companyUnitCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of filteredUnits) {
      if (!t.company_id) continue;
      map.set(t.company_id, (map.get(t.company_id) ?? 0) + 1);
    }
    return map;
  }, [filteredUnits]);

  // Helper to get unit count for a company (O(1) lookup instead of O(n) filter)
  const getCompanyUnitCount = (companyId: string) => companyUnitCounts.get(companyId) ?? 0;

  // Helper to render companies grouped by type
  const renderGroupedCompanies = () => {
    const typeLabels: Record<IndustryType, string> = {
      film: 'Film',
      event: 'Event',
      gov: 'Government',
      utility: 'Utility',
      relief: 'Relief'
    };

    // Helper to determine company types
    const getCompanyTypes = (companyId: string): IndustryType[] => {
      const companyUnits = filteredUnits.filter((t: Unit | UnitFilterData) => t.company_id === companyId);
      if (companyUnits.length === 0) return [];

      // Get all unique types
      const types = new Set<IndustryType>();
      companyUnits.forEach((t: Unit | UnitFilterData) => {
        types.add(t.industry_type || 'film');
      });
      
      return Array.from(types);
    };

    const companiesByType: Record<string, typeof companies> = {};

    companies.forEach((company) => {
      const types = getCompanyTypes(company.id);
      types.forEach(type => {
        if (!companiesByType[type]) companiesByType[type] = [];
        companiesByType[type].push(company);
      });
    });

    // Filter types that have companies and are in the selected types
    const availableTypes = allTypes.filter(type => 
      companiesByType[type] && 
      companiesByType[type].length > 0 &&
      selectedIndustryTypes.includes(type)
    );

    return availableTypes.map((type, index) => {
      // Calculate total units for this type
      const typeUnitCount = companiesByType[type].reduce((sum: number, company) => {
        // Count units for this company AND this type
        return sum + filteredUnits.filter((t: Unit | UnitFilterData) => t.company_id === company.id && (t.industry_type || 'film') === type).length;
      }, 0);

      // Sort companies within this type
      const sortedTypeCompanies = [...companiesByType[type]].sort((a, b) => {
        if (companySortBy === 'count') {
          // Sort by count of THIS type
          const countA = filteredUnits.filter((t: Unit | UnitFilterData) => t.company_id === a.id && (t.industry_type || 'film') === type).length;
          const countB = filteredUnits.filter((t: Unit | UnitFilterData) => t.company_id === b.id && (t.industry_type || 'film') === type).length;
          const countDiff = countB - countA;
          if (countDiff !== 0) return countDiff;
        }
        return a.name.localeCompare(b.name);
      });
      
      return (
      <div key={type}>
        {/* Always show separator for visual distinction, or only if index > 0? 
            User asked for dividers, so dividers between groups + headers is good. */}
        {index > 0 && <DropdownMenuSeparator />}
        <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider bg-muted/50 py-1 flex items-center justify-between px-2">
          <span>{typeLabels[type]}</span>
          <span className="text-xs">({typeUnitCount})</span>
        </DropdownMenuLabel>
        {sortedTypeCompanies.map(company => {
           // PERFORMANCE: O(1) lookup instead of O(n) filter
           const unitCount = getUnitCountByCompanyAndType(company.id, type);
           return (
              <DropdownMenuCheckboxItem
                key={company.id}
                checked={selectedCompanyIds.includes(company.id)}
                onCheckedChange={() => handleCompanyToggle(company.id)}
                onSelect={(e) => e.preventDefault()}
                className="cursor-pointer justify-start pl-6"
              >
                <span className="flex items-center w-full">
                  <span className="flex-1">{company.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">({unitCount})</span>
                </span>
              </DropdownMenuCheckboxItem>
           );
        })}
      </div>
      );
    });
  };

  // Pre-filtered units for Company badge
  // Only filter by PARENT filters (Industry) - NOT by child filters (Group, Model, Status)
  // This ensures badge count is stable when child filters change
  const unitsForCompanyBadge = useMemo(() => {
    return preFilterUnits({
      units: unitsSource,
      selectedIndustryTypes,
      selectedCompanyIds: undefined, // Don't filter by company - that's what this dropdown controls
      selectedGroupIds: undefined, // Don't filter by child filter
      selectedModels: undefined, // Don't filter by child filter
      selectedStatuses: undefined, // Don't filter by child filter
      getLiveStatus: undefined,
    });
  }, [unitsSource, selectedIndustryTypes]);

  // Pre-filtered units for Group badge
  // Only filter by PARENT filters (Industry, Company) - NOT by child filters (Model, Status)
  // This ensures badge count is stable when child filters change
  // In sidebar/override mode (props provided), use the provided units which are already filtered
  const unitsForGroupBadge = useMemo(() => {
    // If units are provided via props (sidebar mode), use them directly as they're already filtered
    if (units.length > 0) {
      return units;
    }
    // Otherwise, pre-filter from context
    return preFilterUnits({
      units: unitsSource,
      selectedIndustryTypes,
      selectedCompanyIds: selectedCompanyIds,
      selectedGroupIds: undefined, // Don't filter by group - that's what this dropdown controls
      selectedModels: undefined, // Don't filter by child filter
      selectedStatuses: undefined, // Don't filter by child filter
      getLiveStatus: undefined,
    });
  }, [units, unitsSource, selectedIndustryTypes, selectedCompanyIds]);

  // Calculate effective selected counts for badges
  // These count items that have matching units given PARENT filter selections only
  // (to ensure counts are stable when child filters change)
  // Returns { selected, total } so we can show badge only when filter is ACTIVE (selected < total)
  const companyCounts = useMemo(() => {
    if (selectedIndustryTypes.length === 0) return { selected: 0, total: 0 };

    // Get set of available companies from pre-filtered units
    const availableCompanies = new Set(unitsForCompanyBadge.map(t => t.company_id).filter(Boolean));

    // Check if we have unassigned units
    const hasUnassigned = unitsForCompanyBadge.some(t => !t.company_id);

    // Total available = companies with units + unassigned if present
    const total = availableCompanies.size + (hasUnassigned ? 1 : 0);

    let selected = selectedCompanyIds.filter((id: string) => availableCompanies.has(id)).length;

    // Add unassigned if selected and available
    if (selectedCompanyIds.includes('__unassigned__') && hasUnassigned) {
      selected++;
    }

    return { selected, total };
  }, [selectedCompanyIds, unitsForCompanyBadge, selectedIndustryTypes]);

  // For backward compatibility - these are used in badge display logic
  const effectiveSelectedCompanyCount = companyCounts.selected;
  // Badge shows when filter is ACTIVE (selected < total) or when there's data (total > 0)
  const showCompanyBadge = effectiveSelectedCompanyCount > 0;

  // Returns { selected, total } so we can show badge only when filter is ACTIVE (selected < total)
  const groupCounts = useMemo(() => {
    if (selectedIndustryTypes.length === 0) return { selected: 0, total: 0 };

    // PERFORMANCE: Uses memoized groupById from component level for O(1) lookups
    // Build a map from group ID to its root group ID in O(n * depth)
    const groupToRoot = new Map<string, string>();
    groups.forEach(g => {
      if (!g.parent_group_id) {
        // This is a root group
        groupToRoot.set(g.id, g.id);
      } else {
        // Trace up to root using the memoized lookup map (O(depth) per group)
        let current = g;
        while (current.parent_group_id) {
          const parent = groupById.get(current.parent_group_id);
          if (!parent) break;
          current = parent;
        }
        groupToRoot.set(g.id, current.id);
      }
    });

    // Check which root groups have matching units (directly or via children)
    // Handle both Unit type (primary_group.id) and UnitFilterData type (primary_group_id)
    const availableRootGroups = new Set<string>();
    unitsForGroupBadge.forEach(unit => {
      const groupId = unit.primary_group_id || ('primary_group' in unit && unit.primary_group?.id);
      if (groupId) {
        const rootId = groupToRoot.get(groupId);
        if (rootId) {
          availableRootGroups.add(rootId);
        }
      }
    });

    // Count root groups that have at least one descendant (or itself) selected
    const selectedRootGroups = new Set<string>();
    selectedGroupIds.forEach(id => {
      // Check if this is an unassigned group
      if (id.startsWith('__unassigned_') && id.endsWith('__')) {
        const companyId = id.slice(13, -2); // Extract company ID from __unassigned_${companyId}__
        // Handle both flat company_id and nested company.id for matching
        const hasUnassigned = unitsForGroupBadge.some(t => {
          const unitCompanyId = t.company_id || ('company' in t && t.company?.id);
          const unitGroupId = t.primary_group_id || ('primary_group' in t && t.primary_group?.id);
          return unitCompanyId === companyId && !unitGroupId;
        });
        if (hasUnassigned) {
          selectedRootGroups.add(id); // Count unassigned as its own "root"
        }
        return;
      }

      // Map selected group to its root
      const rootId = groupToRoot.get(id);
      if (rootId && availableRootGroups.has(rootId)) {
        selectedRootGroups.add(rootId);
      }
    });

    return { selected: selectedRootGroups.size, total: availableRootGroups.size };
  }, [selectedGroupIds, unitsForGroupBadge, selectedIndustryTypes, groups, groupById]);

  // For backward compatibility - these are used in badge display logic
  const effectiveSelectedGroupCount = groupCounts.selected;
  // Badge shows when there's any selection (shows count of selected items)
  const showGroupBadge = effectiveSelectedGroupCount > 0;

  // Trigger button for Company filter
  const companyTriggerButton = (
    <button
      ref={companyButtonRef}
      className={cn(
        "flex items-center gap-1.5 rounded-md bg-muted/40 hover:bg-accent transition-all duration-200 border border-border/50 hover:border-border text-muted-foreground hover:text-foreground",
        isMobile ? 'h-9 px-1.5' : 'px-3 xl:px-4 py-2',
        highlightCompany && "ring-2 ring-[#8FB560] ring-offset-2 ring-offset-background"
      )}
    >
      <Building2 className="h-4 w-4" />
      {!isMobile && <span className="tracking-wide text-xs hidden xl:inline">COMPANY</span>}
      {!filterContextLoading && showCompanyBadge && (
        <span className="text-[10px] leading-none bg-[#8FB560] text-black rounded-full px-1.5 py-0.5 min-w-[16px] text-center font-semibold">
          {effectiveSelectedCompanyCount}
        </span>
      )}
      <ChevronDown className="h-3.5 w-3.5" />
    </button>
  );

  // Mobile company filter content with custom checkboxes
  const renderMobileCompanyContent = () => {
    if (!shouldGroupByType) {
      return (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          Select an industry first
        </div>
      );
    }

    const typeLabels: Record<IndustryType, string> = {
      film: 'Film', event: 'Event', gov: 'Government', utility: 'Utility', relief: 'Relief'
    };

    const getCompanyTypes = (companyId: string): IndustryType[] => {
      const companyUnits = filteredUnits.filter((t: Unit | UnitFilterData) => t.company_id === companyId);
      if (companyUnits.length === 0) return [];
      const types = new Set<IndustryType>();
      companyUnits.forEach((t: Unit | UnitFilterData) => types.add(t.industry_type || 'film'));
      return Array.from(types);
    };

    const companiesByType: Record<string, typeof companies> = {};
    companies.forEach(company => {
      const types = getCompanyTypes(company.id);
      types.forEach(type => {
        if (!companiesByType[type]) companiesByType[type] = [];
        companiesByType[type].push(company);
      });
    });

    const allTypes: IndustryType[] = ['film', 'event', 'gov', 'utility', 'relief'];
    const availableTypes = allTypes.filter(type =>
      companiesByType[type] && companiesByType[type].length > 0 && selectedIndustryTypes.includes(type)
    );

    return (
      <>
        <div className="flex items-center justify-between px-4 py-3 bg-popover z-10 border-b sticky top-0">
          <button
            className="text-[10px] text-muted-foreground uppercase cursor-pointer hover:text-foreground flex items-center gap-1 group font-semibold"
            onClick={() => setCompanySortBy(prev => prev === 'name' ? 'count' : 'name')}
          >
            Companies
            {companySortBy === 'count' ? (
              <ArrowDownWideNarrow className="w-3 h-3 text-primary" />
            ) : (
              <ArrowDownAZ className="w-3 h-3 opacity-0 group-hover:opacity-50" />
            )}
          </button>
          <div className="flex items-center gap-1.5">
            <button onClick={handleSelectAllCompanies} className="text-xs text-muted-foreground hover:text-primary hover:underline">All</button>
            <span className="text-xs text-[#8FB560]">|</span>
            <button onClick={handleClearCompanies} className="text-xs text-primary hover:underline">Clear</button>
          </div>
        </div>
        <div className="px-4 py-2">
          {availableTypes.map((type, index) => {
            const typeCompanies = companiesByType[type] || [];
            const sortedCompanies = [...typeCompanies].sort((a, b) => {
              if (companySortBy === 'count') {
                const countA = filteredUnits.filter((t: Unit | UnitFilterData) => t.company_id === a.id && (t.industry_type || 'film') === type).length;
                const countB = filteredUnits.filter((t: Unit | UnitFilterData) => t.company_id === b.id && (t.industry_type || 'film') === type).length;
                return countB - countA;
              }
              return a.name.localeCompare(b.name);
            });

            const typeTotal = sortedCompanies.reduce((sum, c) =>
              sum + filteredUnits.filter((t: Unit | UnitFilterData) => t.company_id === c.id && (t.industry_type || 'film') === type).length, 0);

            return (
              <div key={type} className={cn(index > 0 && "mt-4 pt-3 border-t border-border")}>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider bg-muted py-1.5 px-2 mb-2 flex justify-between">
                  <span>{typeLabels[type]}</span>
                  <span>({typeTotal})</span>
                </div>
                {sortedCompanies.map(company => {
                  // PERFORMANCE: O(1) lookup instead of O(n) filter
                  const unitCount = getUnitCountByCompanyAndType(company.id, type);
                  const isSelected = selectedCompanyIds.includes(company.id);
                  return (
                    <button
                      key={company.id}
                      onClick={() => handleCompanyToggle(company.id)}
                      className="flex items-center justify-between w-full py-3 border-b border-border/50 last:border-b-0"
                    >
                      <span className="flex items-center gap-3">
                        <span className={cn(
                          "flex h-5 w-5 items-center justify-center rounded border",
                          isSelected ? "bg-[#8FB560] border-[#8FB560]" : "border-muted-foreground/50"
                        )}>
                          {isSelected && <Check className="h-3.5 w-3.5 text-black" />}
                        </span>
                        <span className="text-sm">{company.name}</span>
                      </span>
                      <span className="text-xs text-muted-foreground">({unitCount})</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          {/* Unassigned */}
          {(() => {
            const unassignedCount = filteredUnits.filter((t: Unit | UnitFilterData) => !t.company_id).length;
            if (unassignedCount === 0) return null;
            const isSelected = selectedCompanyIds.includes('__unassigned__');
            return (
              <div className="mt-4 pt-3 border-t border-border">
                <button
                  onClick={() => handleCompanyToggle('__unassigned__')}
                  className="flex items-center justify-between w-full py-3 bg-muted/30 rounded px-2"
                >
                  <span className="flex items-center gap-3">
                    <span className={cn(
                      "flex h-5 w-5 items-center justify-center rounded border",
                      isSelected ? "bg-[#8FB560] border-[#8FB560]" : "border-muted-foreground/50"
                    )}>
                      {isSelected && <Check className="h-3.5 w-3.5 text-black" />}
                    </span>
                    <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground italic">Unassigned</span>
                  </span>
                  <span className="text-xs text-muted-foreground">({unassignedCount})</span>
                </button>
              </div>
            );
          })()}
        </div>
      </>
    );
  };

  // Company filter - Desktop dropdown
  const companyDropdownContent = !shouldGroupByType ? (
    <div className="px-2 py-8 text-center text-sm text-muted-foreground">
      Select an industry first
    </div>
  ) : (
    <>
      <div className="flex items-center justify-between px-2 py-1.5 bg-popover z-10 border-b flex-shrink-0">
        <DropdownMenuLabel
          className="text-[10px] text-muted-foreground uppercase p-0 cursor-pointer hover:text-foreground flex items-center gap-1 group"
          onClick={() => setCompanySortBy(prev => prev === 'name' ? 'count' : 'name')}
        >
          Companies
          {companySortBy === 'count' ? (
            <ArrowDownWideNarrow className="w-3 h-3 text-primary" />
          ) : (
            <ArrowDownAZ className="w-3 h-3 opacity-0 group-hover:opacity-50" />
          )}
        </DropdownMenuLabel>
        <div className="flex items-center gap-1.5">
          <button onClick={handleSelectAllCompanies} className="text-xs text-muted-foreground hover:text-primary hover:underline">All</button>
          <span className="text-xs text-[#8FB560]">|</span>
          <button onClick={handleClearCompanies} className="text-xs text-primary hover:underline">Clear</button>
        </div>
      </div>
      <div className="max-h-[calc(80vh-3rem)] overflow-y-auto px-1 py-1 pb-2">
        {renderGroupedCompanies()}
        {(() => {
          const unassignedCount = filteredUnits.filter((t: Unit | UnitFilterData) => !t.company_id).length;
          if (unassignedCount === 0) return null;
          return (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={selectedCompanyIds.includes('__unassigned__')}
                onCheckedChange={() => handleCompanyToggle('__unassigned__')}
                onSelect={(e) => e.preventDefault()}
                className="cursor-pointer bg-muted/30 data-[state=checked]:bg-muted/50 justify-start"
              >
                <span className="flex items-center w-full gap-2">
                  <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground italic flex-1">Unassigned</span>
                  <span className="text-xs text-muted-foreground ml-2">({unassignedCount})</span>
                </span>
              </DropdownMenuCheckboxItem>
            </>
          );
        })()}
      </div>
    </>
  );

  return (
    <div className={`flex items-center ${sidebarMode ? 'w-full' : isMobile ? 'gap-2' : 'gap-1.5'}`}>
      {/* Company Selector - Multi-select with static label - NEVER shown in sidebar mode or embedded mode */}
      {showCompanyFilter && !sidebarMode && !embeddedMode && (
        isMobile ? (
          <Sheet open={companyMenuOpen} onOpenChange={setCompanyMenuOpen}>
            <SheetTrigger asChild>
              {companyTriggerButton}
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[100dvh] p-0 rounded-t-xl">
              <SheetHeader className="px-4 py-4 border-b border-border">
                <SheetTitle className="text-lg font-semibold">Company Filter</SheetTitle>
              </SheetHeader>
              <div className="overflow-y-auto flex-1 h-[calc(100dvh-65px)]">
                {renderMobileCompanyContent()}
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          <DropdownMenu modal={false} open={companyMenuOpen} onOpenChange={setCompanyMenuOpen}>
            <DropdownMenuTrigger asChild>
              {companyTriggerButton}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[296px] p-0">
              {companyDropdownContent}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      )}

      {/* Group Selector - Multi-select with static label */}
      {/* Hidden when hideGroupFilter is true (Reports status bar) or showGroupFilter is false (permissions) */}
      {showGroupFilter && !hideGroupFilter && (
        (() => {
          // Trigger button for Group filter
          const groupTriggerButton = (
            <button
              ref={groupButtonRef}
              className={cn(
                "flex items-center gap-1.5 rounded-md bg-muted/40 hover:bg-accent transition-all duration-200 border border-border/50 hover:border-border text-muted-foreground hover:text-foreground",
                sidebarMode
                  ? "w-full h-8 px-3"
                  : isMobile
                    ? "h-9 px-1.5"
                    : "px-3 xl:px-4 py-2",
                highlightGroup && "ring-2 ring-[#8FB560] ring-offset-2 ring-offset-background"
              )}
            >
              <GroupIcon className="h-4 w-4" />
              {sidebarMode ? (
                <span className="tracking-wide text-xs uppercase flex-1 text-left truncate">{groupLabel.toUpperCase()}</span>
              ) : isGroupAdmin ? (
                <span className="tracking-wide text-xs">{groupLabel.toUpperCase()}</span>
              ) : (
                !isMobile && <span className="tracking-wide text-xs hidden xl:inline">{groupLabel.toUpperCase()}</span>
              )}
              {showGroupBadge && (
                <span className="text-[10px] leading-none bg-[#8FB560] text-black rounded-full px-1.5 py-0.5 min-w-[16px] text-center font-semibold">
                  {effectiveSelectedGroupCount}
                </span>
              )}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          );

        // Header for Group filter
        const groupFilterHeader = (isSheetMode: boolean) => (
          <div className={cn(
            "flex items-center justify-between sticky top-0 bg-popover z-10 border-b border-border flex-shrink-0",
            isSheetMode ? "px-4 py-3" : "px-2 py-1.5",
            embeddedMode && "rounded-t-md"
          )}>
            <button
              className="text-[10px] text-muted-foreground uppercase cursor-pointer hover:text-foreground flex items-center gap-1 group font-semibold"
              onClick={() => setGroupSortBy(prev => prev === 'name' ? 'count' : 'name')}
            >
              {groupLabel}s
              {groupSortBy === 'count' ? (
                <ArrowDownWideNarrow className="w-3 h-3 text-primary" />
              ) : (
                <ArrowDownAZ className="w-3 h-3 opacity-0 group-hover:opacity-50" />
              )}
            </button>
            <div className="flex items-center gap-1.5">
              <button onClick={handleSelectAllGroups} className="text-xs text-muted-foreground hover:text-primary hover:underline">All</button>
              <span className="text-xs text-[#8FB560]">|</span>
              <button onClick={handleClearGroups} className="text-xs text-primary hover:underline">Clear</button>
            </div>
          </div>
        );

        // Embedded mode: Just render content without dropdown wrapper (for use inside Popovers)
        // Uses flex-col to stack header above content vertically
        if (embeddedMode) {
          return (
            <div className="flex flex-col w-full">
              {!shouldGroupByType ? (
                <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                  Select an industry first
                </div>
              ) : (
                <>
                  {groupFilterHeader(false)}
                  <div className="max-h-[calc(50vh-3rem)] overflow-y-auto px-1 py-1 pb-2">
                    {renderGroupTreeContent()}
                  </div>
                </>
              )}
            </div>
          );
        }

        // Mobile (non-sidebar): Full-screen bottom sheet
        if (isMobile && !sidebarMode) {
          return (
            <Sheet open={groupMenuOpen} onOpenChange={setGroupMenuOpen}>
              <SheetTrigger asChild>
                {groupTriggerButton}
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[100dvh] p-0 rounded-t-xl">
                <SheetHeader className="px-4 py-4 border-b border-border">
                  <SheetTitle className="text-lg font-semibold">{groupLabel} Filter</SheetTitle>
                </SheetHeader>
                <div className="overflow-y-auto flex-1 h-[calc(100dvh-65px)]">
                  {!shouldGroupByType ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                      Select an industry first
                    </div>
                  ) : (
                    <>
                      {groupFilterHeader(true)}
                      <div className={isMobile && !sidebarMode ? "px-0" : ""}>
                        {renderGroupTreeContent()}
                      </div>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          );
        }

        // Desktop or sidebar mode: Regular dropdown
        return (
          <DropdownMenu modal={false} open={groupMenuOpen} onOpenChange={setGroupMenuOpen}>
            <DropdownMenuTrigger asChild>
              {groupTriggerButton}
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className={cn(
                "w-[320px] p-0",
                sidebarMode && "max-h-[50vh] overflow-y-auto"
              )}
            >
              {!shouldGroupByType ? (
                <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                  Select an industry first
                </div>
              ) : (
                <>
                  {groupFilterHeader(false)}
                  <div className={cn(
                    "overflow-y-auto px-1 py-1 pb-2",
                    sidebarMode ? "max-h-[calc(50vh-3rem)]" : "max-h-[calc(80vh-3rem)]"
                  )}>
                    {renderGroupTreeContent()}
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );

        // Helper function to render group tree content (shared by both mobile and desktop)
        function renderGroupTreeContent() {
          if (groups.length === 0) {
            return (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                {selectedCompanyIds.length === 0
                  ? 'Select a company first'
                  : 'No groups available'
                }
              </div>
            );
          }

          let renderedCount = 0;

          // Helper to count total units in a group (including all descendants)
          // PERFORMANCE: Uses O(1) lookup for leaf nodes instead of O(n) filter
          const getTotalUnits = (group: GroupWithChildren): number => {
            if (!group.children || group.children.length === 0) {
              return getUnitCountByGroup(group.id);
            }
            return group.children.reduce((sum: number, child) => sum + getTotalUnits(child), 0);
          };

          // Helper to count selected units in a group
          // PERFORMANCE: Uses O(1) lookup for leaf nodes instead of O(n) filter
          const getSelectedUnits = (group: GroupWithChildren): number => {
            if (!group.children || group.children.length === 0) {
              if (selectedGroupIds.includes(group.id)) {
                return getUnitCountByGroup(group.id);
              }
              return 0;
            }
            return group.children.reduce((sum: number, child) => sum + getSelectedUnits(child), 0);
          };

          // Helper to render a group item
          const renderGroupItem = (group: GroupWithChildren, isChild = false, indentLevel = 0): React.ReactNode => {
            const selectedUnits = getSelectedUnits(group);
            const totalUnits = getTotalUnits(group);
            const selectionState = getGroupSelectionState(group);
            const hasChildren = group.children && group.children.length > 0;

            const sortedChildren = hasChildren ? [...(group.children as GroupWithChildren[])].sort((a, b) => {
              if (groupSortBy === 'count') return getTotalUnits(b) - getTotalUnits(a);
              return a.name.localeCompare(b.name);
            }) : [];

            // Mobile full-screen sheet: use larger touch-friendly styling
            if (isMobile && !sidebarMode) {
              if (hasChildren) {
                const isExpanded = expandedGroupIds.has(group.id);
                return (
                  <div key={group.id}>
                    <div
                      className="relative flex items-center cursor-pointer select-none rounded-sm py-3 text-sm outline-none"
                      style={{ paddingLeft: `${indentLevel * 16}px` }}
                    >
                      <button
                        type="button"
                        className="flex items-center gap-3 flex-1 min-w-0 pr-2"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGroupWithCascade(group.id); }}
                      >
                        <span className={cn(
                          "flex h-5 w-5 items-center justify-center rounded border shrink-0",
                          selectionState.selected ? "bg-[#8FB560] border-[#8FB560]" : "border-muted-foreground/50"
                        )}>
                          {selectionState.selected && <Check className="h-3.5 w-3.5 text-black" />}
                          {selectionState.partiallySelected && !selectionState.selected && (
                            <Minus className="h-3 w-3 text-muted-foreground" />
                          )}
                        </span>
                        <span className="truncate">{group.name}</span>
                      </button>
                      <button
                        type="button"
                        className="flex items-center gap-1 shrink-0 px-1 py-0.5 rounded"
                        onClick={(e) => toggleGroupExpanded(group.id, e)}
                      >
                        <span className="text-xs text-muted-foreground">({totalUnits})</span>
                        <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="border-l border-border/50 ml-4">
                        {sortedChildren.map((child) => renderGroupItem(child, true, indentLevel + 1))}
                      </div>
                    )}
                  </div>
                );
              }

              // Leaf node on mobile
              const isSelected = selectedGroupIds.includes(group.id);
              return (
                <button
                  key={group.id}
                  onClick={() => toggleGroupWithCascade(group.id)}
                  className="flex items-center justify-between w-full py-3 border-b border-border/50 last:border-b-0"
                  style={{ paddingLeft: `${indentLevel * 16}px` }}
                >
                  <span className="flex items-center gap-3">
                    <span className={cn(
                      "flex h-5 w-5 items-center justify-center rounded border",
                      isSelected ? "bg-[#8FB560] border-[#8FB560]" : "border-muted-foreground/50"
                    )}>
                      {isSelected && <Check className="h-3.5 w-3.5 text-black" />}
                    </span>
                    <span className="text-sm">{group.name}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">({totalUnits})</span>
                </button>
              );
            }

            // Embedded mode: use tight desktop-like styling (no DropdownMenuSub available)
            if (embeddedMode) {
              if (hasChildren) {
                const isExpanded = expandedGroupIds.has(group.id);
                return (
                  <div key={group.id}>
                    <div
                      className="relative flex items-center cursor-pointer select-none rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGroupWithCascade(group.id); }}
                    >
                      <span className="flex items-center justify-between w-full">
                        <span className="flex items-center gap-2">
                          <span className="flex h-3.5 w-3.5 items-center justify-center shrink-0">
                            {selectionState.selected ? (
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                              </svg>
                            ) : selectionState.partiallySelected ? (
                              <Minus className="h-3 w-3 text-muted-foreground" />
                            ) : null}
                          </span>
                          <span className="truncate">{group.name}</span>
                        </span>
                        <button
                          type="button"
                          className="flex items-center gap-1 shrink-0 ml-2"
                          onClick={(e) => { e.stopPropagation(); toggleGroupExpanded(group.id, e); }}
                        >
                          <span className="text-xs text-muted-foreground">({totalUnits})</span>
                          <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="ml-4 pl-2 border-l border-border/50">
                        {sortedChildren.map((child) => renderGroupItem(child, true, indentLevel + 1))}
                      </div>
                    )}
                  </div>
                );
              }

              // Leaf node in embedded mode - tight desktop styling
              const isSelected = selectedGroupIds.includes(group.id);
              return (
                <div
                  key={group.id}
                  onClick={() => toggleGroupWithCascade(group.id)}
                  className="relative flex items-center select-none rounded-sm px-2 py-1.5 text-sm outline-none cursor-pointer hover:bg-accent"
                  style={{ paddingLeft: indentLevel > 0 ? `${8 + indentLevel * 12}px` : undefined }}
                >
                  <span className="flex items-center justify-between w-full">
                    <span className="flex items-center gap-2">
                      <span className="flex h-3.5 w-3.5 items-center justify-center shrink-0">
                        {isSelected && (
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                          </svg>
                        )}
                      </span>
                      <span>{group.name}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">({totalUnits})</span>
                  </span>
                </div>
              );
            }

            // Desktop/sidebar: use DropdownMenu components
            // When searching and this group matches, render inline expanded instead of hover submenu
            const isSearchExpanded = searchQuery && expandedGroupIds.has(group.id);
            
            if (hasChildren && !isChild) {
              // If search is active and this group is expanded, render inline with collapsible
              if (isSearchExpanded) {
                return (
                  <div key={group.id} className="relative">
                    <div
                      className="relative flex items-center select-none rounded-sm px-2 py-1.5 text-sm outline-none cursor-pointer hover:bg-accent"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGroupWithCascade(group.id); }}
                    >
                      <span className="flex items-center justify-between w-full">
                        <span className="flex items-center gap-2">
                          <span className="flex h-3.5 w-3.5 items-center justify-center shrink-0">
                            {selectionState.selected ? (
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                              </svg>
                            ) : selectionState.partiallySelected ? (
                              <Minus className="h-3 w-3 text-muted-foreground" />
                            ) : null}
                          </span>
                          <span>{group.name}</span>
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {totalUnits === 0 ? '(0)' : selectedUnits === totalUnits ? `(${selectedUnits}/${totalUnits})` : (<>(<span className="text-[#8FB560]">{selectedUnits}</span>/{totalUnits})</>)}
                        </span>
                      </span>
                    </div>
                    {/* Render children inline when search-expanded */}
                    <div className="pl-4 border-l border-border/50 ml-3">
                      {sortedChildren.map((child) => renderGroupItem(child, true))}
                    </div>
                  </div>
                );
              }
              
              // Default: hover-based pop-out submenu
              return (
                <DropdownMenuSub key={group.id}>
                  <DropdownMenuSubTrigger
                    className="cursor-pointer"
                    onClick={(e) => {
                      if (!(e.target as HTMLElement).closest('.submenu-arrow')) {
                        e.preventDefault(); e.stopPropagation();
                        toggleGroupWithCascade(group.id);
                      }
                    }}
                  >
                    <span className="flex items-center justify-between w-full">
                      <span className="flex items-center gap-2">
                        <span className="flex h-3.5 w-3.5 items-center justify-center shrink-0">
                          {selectionState.selected ? (
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                            </svg>
                          ) : selectionState.partiallySelected ? (
                            <Minus className="h-3 w-3 text-muted-foreground" />
                          ) : null}
                        </span>
                        <span>{group.name}</span>
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {totalUnits === 0 ? '(0)' : selectedUnits === totalUnits ? `(${selectedUnits}/${totalUnits})` : (<>(<span className="text-[#8FB560]">{selectedUnits}</span>/{totalUnits})</>)}
                      </span>
                    </span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {sortedChildren.map((child) => renderGroupItem(child, true))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              );
            }

            return (
              <DropdownMenuCheckboxItem
                key={group.id}
                checked={selectedGroupIds.includes(group.id)}
                onCheckedChange={() => toggleGroupWithCascade(group.id)}
                onSelect={(e) => e.preventDefault()}
                className="cursor-pointer justify-start"
              >
                <span className="flex items-center w-full">
                  <span className="flex-1">{group.name}</span>
                  <span className="text-xs text-muted-foreground ml-2">({totalUnits})</span>
                </span>
              </DropdownMenuCheckboxItem>
            );
          };

          // Filter and render company sections
          // In embedded mode, show all groups regardless of unit count
          const filterTree = (nodes: GroupWithChildren[], getTotalUnitsFunc: (g: GroupWithChildren) => number): GroupWithChildren[] => {
            if (embeddedMode) {
              // Don't filter by unit count in embedded mode - show all groups
              return nodes.map(node => ({
                ...node,
                children: node.children ? filterTree(node.children, getTotalUnitsFunc) : []
              }));
            }
            return nodes.filter(node => getTotalUnitsFunc(node) > 0).map(node => ({
              ...node,
              children: node.children ? filterTree(node.children, getTotalUnitsFunc) : []
            }));
          };

          const companySections = companies
            // In embedded mode, show all companies; otherwise filter by selectedCompanyIds
            .filter((company: Company) => embeddedMode || selectedCompanyIds.includes(company.id))
            .sort((a: Company, b: Company) => groupSortBy === 'count' ? getCompanyUnitCount(b.id) - getCompanyUnitCount(a.id) : a.name.localeCompare(b.name))
            .map((company: Company) => {
              const companyGroupTree = groupTree
                .filter(g => g.company_id === company.id)
                .sort((a, b) => groupSortBy === 'count' ? getTotalUnits(b) - getTotalUnits(a) : a.name.localeCompare(b.name));

              // PERFORMANCE: O(1) lookup for unassigned count instead of O(n) filter
              const unassignedCount = getUnassignedCountByCompany(company.id);
              const unassignedInCompany = unassignedCount > 0 ? { length: unassignedCount } : { length: 0 };
              const filteredTree = filterTree(companyGroupTree, getTotalUnits);

              // In embedded mode, show company even if no units (groups may still exist)
              if (!embeddedMode && filteredTree.length === 0 && unassignedInCompany.length === 0) return null;
              if (embeddedMode && filteredTree.length === 0) return null;

              const shouldShowSeparator = renderedCount > 0;
              renderedCount++;

              // Mobile full-screen rendering (touch-friendly)
              if (isMobile && !sidebarMode) {
                return (
                  <div key={company.id} className={cn(shouldShowSeparator && "mt-4 pt-3 border-t border-border")}>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider bg-muted py-1.5 px-2 mb-2">
                      {company.name}
                    </div>
                    {filteredTree.map(group => renderGroupItem(group))}
                    {unassignedInCompany.length > 0 && (() => {
                      const isSelected = selectedGroupIds.includes(`__unassigned_${company.id}__`);
                      return (
                        <button
                          onClick={() => toggleGroupWithCascade(`__unassigned_${company.id}__`)}
                          className="flex items-center justify-between w-full py-3 bg-muted/30 rounded px-2 mt-2"
                        >
                          <span className="flex items-center gap-3">
                            <span className={cn(
                              "flex h-5 w-5 items-center justify-center rounded border",
                              isSelected ? "bg-[#8FB560] border-[#8FB560]" : "border-muted-foreground/50"
                            )}>
                              {isSelected && <Check className="h-3.5 w-3.5 text-black" />}
                            </span>
                            <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground italic">Unassigned</span>
                          </span>
                          <span className="text-xs text-muted-foreground">({unassignedInCompany.length})</span>
                        </button>
                      );
                    })()}
                  </div>
                );
              }

              // Embedded mode rendering (tight desktop styling, no company headers, no DropdownMenuSub)
              if (embeddedMode) {
                // Just render groups directly without company header
                return (
                  <div key={company.id}>
                    {filteredTree.map(group => renderGroupItem(group))}
                  </div>
                );
              }

              // Desktop/sidebar dropdown rendering
              return (
                <div key={company.id}>
                  {shouldShowSeparator && <DropdownMenuSeparator />}
                  <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider bg-muted/50 py-1">
                    {company.name}
                  </DropdownMenuLabel>
                  {filteredTree.map(group => renderGroupItem(group))}
                  {unassignedInCompany.length > 0 && (
                    <DropdownMenuSub key={`__unassigned_${company.id}__`}>
                      <DropdownMenuSubTrigger
                        className="cursor-pointer bg-muted/30 data-[state=open]:bg-muted/50 [&>svg]:text-popover"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGroupWithCascade(`__unassigned_${company.id}__`); }}
                      >
                        <span className="flex items-center justify-between w-full">
                          <span className="flex items-center gap-2">
                            <span className="flex h-3.5 w-3.5 items-center justify-center shrink-0">
                              {selectedGroupIds.includes(`__unassigned_${company.id}__`) && (
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                                  <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                                </svg>
                              )}
                            </span>
                            <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-muted-foreground italic">Unassigned</span>
                          </span>
                          <span className="text-xs text-muted-foreground">({unassignedInCompany.length})</span>
                        </span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="hidden" />
                    </DropdownMenuSub>
                  )}
                </div>
              );
            });

          return companySections;
        }
      })()
      )}
    </div>
  );
}
